import type { TranslationKey } from "@msuite/utils/locales";
import type { Enums } from "@msuite/utils/types";
import { useTranslation } from "../localization";
import { Tag } from "./tag";

/**
 * The reach of one permission grant, as the scope picker offers it. Structural on purpose —
 * this package does not depend on citadel, and the tag only needs to know how each reach is
 * named and coloured. `combined` is the union of the two narrow reaches, which no single
 * scope expresses — it exists only as a display state.
 */
export type PermissionScopeTagValue =
	| "none"
	| "organization"
	| "own"
	| "ownProjects"
	| "custom"
	| "combined";

/** One colour per reach: the wider the grant, the stronger the tone; grey is no access. */
const SCOPE_COLORS: Record<PermissionScopeTagValue, Enums<"color">> = {
	none: "gray",
	organization: "green",
	own: "blue",
	ownProjects: "violet",
	custom: "amber",
	combined: "teal",
};

const SCOPE_LABELS: Record<PermissionScopeTagValue, TranslationKey> = {
	none: "roles.scope_none",
	organization: "roles.scope_organization",
	own: "roles.scope_own",
	ownProjects: "roles.scope_own_projects",
	custom: "roles.scope_custom",
	combined: "roles.scope_combined",
};

/** Props Interface */
interface PermissionScopeTagProps {
	scope: PermissionScopeTagValue;
}

export function PermissionScopeTag({ scope }: PermissionScopeTagProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Render */
	return (
		<Tag
			variant="plain"
			color={SCOPE_COLORS[scope]}
		>
			{t(SCOPE_LABELS[scope])}
		</Tag>
	);
}
